// `mostrar_guardado`: el agente MUESTRA lo que el usuario ya tiene —
// ADR-2026-09-08-mostrar-lo-guardado.
//
// Hasta acá el agente sólo podía mostrar en el chat lo que se GENERABA (o la galería de
// últimas generaciones, `show_recent_media`). "Mostrame mis productos" no tenía respuesta:
// `ver_productos` y `ver_fotos` son para que MIRE el modelo, no para que vea el usuario.
//
// Acá está lo puro: qué tipos hay, cuántos entran, el filtro por nombre y el `[registro:]`
// que le permite al agente mapear "el segundo" a un id sin preguntar nada técnico. El
// fetch de cada fuente vive en la card.

import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

export type TipoGuardado = 'productos' | 'modelos' | 'poses' | 'fondos' | 'subidas';

export const TIPOS_GUARDADO: TipoGuardado[] = [
  'productos',
  'modelos',
  'poses',
  'fondos',
  'subidas',
];

/** Tope duro de miniaturas por card: más que esto no se mira, se scrollea. */
export const MAX_A_MOSTRAR = 24;
export const LIMITE_POR_DEFECTO = 12;

export interface ItemGuardado {
  /** El id de la fuente. Es el que el agente después usa como `product_id`, `model_id`… */
  id: string;
  /** Lo que se lee debajo de la miniatura. Vacío en las subidas: son archivos, no fichas. */
  label: string;
  thumbUrl: string;
  /**
   * URL del ORIGINAL, para descargar. Sólo cuando ya la tenemos gratis: varias fuentes
   * muestran una miniatura transformada y el archivo entero hay que firmarlo aparte, así
   * que se resuelve recién al tocar Descargar y no en cada galería que nadie baja
   * (ADR-2026-09-08-descargar-todo-desde-el-chat).
   */
  fullUrl?: string;
  /** Sólo fondos: el nombre en el bucket, para firmar el original al descargar. */
  fileName?: string;
}

const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** El tipo que pidió el modelo. `null` si mandó cualquier cosa: la card lo dice en vez de
 *  mostrar una lista que el usuario no pidió. */
export function tipoPedido(entrada: unknown): TipoGuardado | null {
  const t = texto((entrada as { tipo?: unknown } | null)?.tipo).toLowerCase();
  return (TIPOS_GUARDADO as string[]).includes(t) ? (t as TipoGuardado) : null;
}

export function limitePedido(entrada: unknown): number {
  const raw = (entrada as { limit?: unknown } | null)?.limit;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return LIMITE_POR_DEFECTO;
  return Math.min(MAX_A_MOSTRAR, Math.max(1, Math.round(n)));
}

export const busquedaPedida = (entrada: unknown): string =>
  texto((entrada as { buscar?: unknown } | null)?.buscar);

/** Sin acentos y en minúsculas: "remera negra" tiene que encontrar "Remera Negra" y
 *  "camión" tiene que encontrarse escribiendo "camion". */
const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Filtra por nombre y recorta al límite. Una búsqueda vacía no filtra nada; una búsqueda
 * sin resultados devuelve vacío (y la card lo dice) en vez de caer a "te muestro todo",
 * que le haría creer al usuario que eso ES lo que buscaba.
 */
export function filtrarGuardado(
  items: ItemGuardado[],
  buscar: string,
  limite: number = LIMITE_POR_DEFECTO,
): ItemGuardado[] {
  const q = normalizar(buscar);
  const filtrados = q ? items.filter((i) => normalizar(i.label).includes(q)) : items;
  return filtrados.slice(0, Math.max(1, limite));
}

export const ETIQUETA_TIPO: Record<
  TipoGuardado,
  { titulo: string; vacio: string; unidad: string }
> = {
  productos: {
    titulo: 'Tus productos',
    vacio: 'Todavía no tenés productos cargados.',
    unidad: 'producto',
  },
  modelos: {
    // "Modelos" en Moda, "Avatares" en el resto: `get` porque la tabla se arma al
    // cargar el módulo y la marca se cambia sin recargar
    // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
    get titulo() {
      return `Tus ${palabraDeModeloActual().plural}`;
    },
    get vacio() {
      return `Todavía no tenés ${palabraDeModeloActual().plural} guardados.`;
    },
    get unidad() {
      return palabraDeModeloActual().singular;
    },
  },
  poses: {
    titulo: 'Tus poses',
    vacio: 'Todavía no guardaste poses propias.',
    unidad: 'pose',
  },
  fondos: {
    titulo: 'Tus fondos',
    vacio: 'Todavía no guardaste fondos propios.',
    unidad: 'fondo',
  },
  subidas: {
    titulo: 'Fotos que subiste al chat',
    vacio: 'Todavía no subiste fotos por acá.',
    unidad: 'foto',
  },
};

/** El texto de "no hay nada", que cambia si el vacío es por la BÚSQUEDA y no por el
 *  catálogo: no es lo mismo "no tenés productos" que "no encontré ese producto". */
export function mensajeVacio(tipo: TipoGuardado, buscar: string, hayEnTotal: boolean): string {
  if (buscar && hayEnTotal) return `No encontré nada que diga "${buscar}".`;
  return ETIQUETA_TIPO[tipo].vacio;
}

/**
 * Lo que va al historial: qué se mostró, en qué ORDEN y con qué id. Es lo que hace que
 * "usá el segundo" funcione sin pedirle al usuario un id que no conoce.
 */
export function registroGuardadoParaHistorial(
  tipo: TipoGuardado,
  items: ItemGuardado[],
): string {
  if (items.length === 0) {
    return `[registro: le mostraste la lista de ${tipo} y estaba VACÍA. No inventes que tiene cosas guardadas.]`;
  }
  const clave =
    tipo === 'productos' ? 'product_id' : tipo === 'modelos' ? 'model_id' : 'id';
  const lista = items
    .map((it, i) => `#${i + 1} ${clave}=${it.id}${it.label ? ` — "${it.label}"` : ''}`)
    .join('; ');
  return `[registro: le mostraste ${items.length} ${ETIQUETA_TIPO[tipo].unidad}(s), EN ESTE ORDEN: ${lista}. Si dice "el segundo", "ese" o lo nombra, mapealo a ese id y usalo directo — NUNCA le pidas el id.]`;
}

// ── Descargar todo ──────────────────────────────────────────────────────────

/** Desde cuántos elementos aparece "Descargar todo". Con uno no hay un "todo" que bajar:
 *  se toca la miniatura. */
export const MINIMO_PARA_DESCARGAR_TODO = 2;

export const puedeDescargarTodo = (items: unknown[]): boolean =>
  items.length >= MINIMO_PARA_DESCARGAR_TODO;

/** Nombre de archivo seguro a partir de una etiqueta: sin acentos, sin barras, sin
 *  espacios. Un nombre con `/` crea carpetas dentro del zip sin que nadie lo haya pedido. */
export function slugDeArchivo(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * Cómo se llama cada archivo dentro del zip. Numerado SIEMPRE por delante, porque el orden
 * en que se ven es el que el usuario tiene en la cabeza ("el tercero"), y porque muchos
 * items no tienen nombre (las subidas) o lo tienen repetido (dos "Remera negra").
 */
export function nombreDeArchivo(
  tipo: TipoGuardado,
  item: { label: string },
  indice: number,
  ext = 'jpg',
): string {
  const n = String(indice + 1).padStart(2, '0');
  const slug = slugDeArchivo(item.label);
  return `${n}-${slug || tipo}.${ext}`;
}

/** Nombre del zip: qué es y de cuándo. */
export function nombreDelZip(tipo: TipoGuardado, ahora: Date = new Date()): string {
  return `atlas-${tipo}-${ahora.toISOString().slice(0, 10)}.zip`;
}
